import React, { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { Linkedin, Instagram, Mail, Heart, MessageCircle, Repeat2, FileText, Sparkles, ArrowRight, PenLine } from 'lucide-react';

// LinkedIn Post Card - Floating left with parallax
const LinkedInCard = ({ scrollYProgress }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const x = useTransform(scrollYProgress, [0, 1], [0, -20]);
  
  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-2xl w-56 p-4 absolute left-[5%] top-[15%] hidden lg:block"
      style={{ y, x }}
      initial={{ opacity: 0, x: -40 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.3 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Linkedin className="w-4 h-4 text-[#0A66C2]" />
        <span className="text-xs font-semibold text-slate-700">LinkedIn Post</span>
      </div>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 flex items-center justify-center text-white text-[10px] font-bold">SC</div>
        <div>
          <p className="text-[11px] font-semibold text-slate-800">Sarah Chen</p>
          <p className="text-[9px] text-slate-400">VP Marketing</p>
        </div>
      </div>
      <p className="text-[10px] text-slate-600 leading-relaxed mb-2">
        Just shipped our Q4 roadmap and the response was incredible 🚀
      </p>
      <p className="text-[10px] text-slate-500 mb-2">Here's what we learned about product storytelling...</p>
      <div className="flex items-center gap-3 pt-2 border-t border-slate-100 text-[9px] text-slate-400">
        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> 2.8K</span>
        <span className="flex items-center gap-1"><MessageCircle className="w-3 h-3" /> 342</span>
      </div>
    </motion.div>
  );
};

// Short Post Card - Floating top right with parallax
const ShortPostCard = ({ scrollYProgress }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -60]);
  const x = useTransform(scrollYProgress, [0, 1], [0, 30]);
  
  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-2xl w-52 p-4 absolute right-[5%] top-[10%] hidden lg:block"
      style={{ y, x }}
      initial={{ opacity: 0, x: 40 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.4 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <PenLine className="w-4 h-4 text-emerald-600" />
        <span className="text-xs font-semibold text-slate-700">Short Post</span>
      </div>
      <div className="space-y-2 text-[10px] text-slate-600">
        <p>Product roadmaps shouldn't be boring feature lists.</p>
        <p>They should tell a story.</p>
        <p>Here's the framework we use</p>
      </div>
      <div className="flex items-center gap-3 pt-2 mt-2 border-t border-slate-100 text-[9px] text-slate-400">
        <span className="flex items-center gap-1"><Repeat2 className="w-3 h-3" /> 892</span>
        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> 4.2K</span>
      </div>
    </motion.div>
  );
};

// Carousel Card - Floating right middle with parallax
const CarouselCard = ({ scrollYProgress }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -100]);
  const x = useTransform(scrollYProgress, [0, 1], [0, 15]);
  
  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-2xl w-48 p-3 absolute right-[8%] top-[45%] hidden lg:block"
      style={{ y, x }}
      initial={{ opacity: 0, x: 40 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.5 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Instagram className="w-4 h-4 text-[#E4405F]" />
        <span className="text-xs font-semibold text-slate-700">Carousel</span>
      </div>
      <div className="bg-gradient-to-br from-violet-500 to-pink-500 rounded-xl p-4 text-center text-white mb-2">
        <p className="text-sm font-bold leading-tight">Product roadmaps that excite ✨</p>
        <p className="text-[9px] mt-1 opacity-80">Swipe for framework →</p>
      </div>
      <div className="flex items-center gap-2 text-[9px] text-slate-400">
        <span className="flex items-center gap-1"><Heart className="w-3 h-3" /> 1.2K</span>
        <span>5 slides</span>
      </div>
    </motion.div>
  );
};

// Newsletter Card - Floating right bottom with parallax
const NewsletterCard = ({ scrollYProgress }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -120]);
  const x = useTransform(scrollYProgress, [0, 1], [0, 25]);
  
  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-2xl w-44 p-3 absolute right-[12%] bottom-[18%] hidden lg:block"
      style={{ y, x }}
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.6 }}
    >
      <div className="flex items-center gap-2 mb-2">
        <Mail className="w-4 h-4 text-indigo-500" />
        <span className="text-xs font-semibold text-slate-700">Newsletter</span>
      </div>
      <div className="bg-slate-50 rounded-lg p-2 mb-2">
        <p className="text-[9px] text-slate-500">🎯 Our Q4</p>
        <p className="text-[10px] text-slate-700 font-medium">Hey [First Name], something exciting...</p>
      </div>
      <div className="flex items-center gap-3 text-[9px]">
        <div><span className="font-bold text-indigo-600">47%</span> <span className="text-slate-400">Open Rate</span></div>
        <div><span className="font-bold text-indigo-600">12%</span> <span className="text-slate-400">CTR</span></div>
      </div>
    </motion.div>
  );
};

// Blog Article Card - Floating bottom left with parallax
const BlogCard = ({ scrollYProgress }) => {
  const y = useTransform(scrollYProgress, [0, 1], [0, -90]);
  const x = useTransform(scrollYProgress, [0, 1], [0, -25]);
  
  return (
    <motion.div 
      className="bg-white rounded-2xl shadow-2xl w-56 p-4 absolute left-[5%] bottom-[15%] hidden lg:block"
      style={{ y, x }}
      initial={{ opacity: 0, x: -40 }}
      whileInView={{ opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: 0.7 }}
    >
      <div className="flex items-center gap-2 mb-3">
        <FileText className="w-4 h-4 text-emerald-500" />
        <span className="text-xs font-semibold text-slate-700">Blog Article</span>
      </div>
      <div className="mb-2">
        <span className="px-2 py-0.5 bg-rose-50 text-rose-500 text-[9px] font-medium rounded-full">Product Strategy</span>
      </div>
      <h4 className="text-sm font-bold text-slate-800 mb-1 leading-snug">The Art of Product Roadmap Communication</h4>
      <p className="text-[10px] text-slate-500 mb-2">How we increased stakeholder buy-in by 340%</p>
      <p className="text-[9px] text-slate-400">8 min read</p>
    </motion.div>
  );
};

// Central Core Idea Element
const CoreIdeaElement = () => (
  <motion.div 
    className="relative"
    initial={{ opacity: 0, scale: 0.8 }}
    whileInView={{ opacity: 1, scale: 1 }}
    viewport={{ once: true }}
    transition={{ duration: 0.6, delay: 0.2 }}
  >
    <div className="w-28 h-28 md:w-32 md:h-32 rounded-3xl bg-gradient-to-br from-violet-500 via-purple-500 to-pink-500 flex flex-col items-center justify-center shadow-2xl shadow-purple-500/30">
      <Sparkles className="w-8 h-8 md:w-10 md:h-10 text-white mb-1" />
      <span className="text-white text-[10px] md:text-xs font-bold tracking-wider">CORE IDEA</span>
    </div>
  </motion.div>
);

const ContentEngineFlow = () => {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end start"]
  });

  return (
    <section 
      ref={containerRef}
      id="content-engine" 
      className="relative min-h-screen overflow-hidden bg-gradient-to-br from-slate-800 via-purple-800/90 to-slate-800"
      data-testid="content-engine-section"
    >
      {/* Background effects */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-violet-400/10 rounded-full blur-3xl" />
      </div>

      {/* Floating Cards with Parallax */}
      <LinkedInCard scrollYProgress={scrollYProgress} />
      <ShortPostCard scrollYProgress={scrollYProgress} />
      <CarouselCard scrollYProgress={scrollYProgress} />
      <NewsletterCard scrollYProgress={scrollYProgress} />
      <BlogCard scrollYProgress={scrollYProgress} />

      {/* Main Content - Centered */}
      <div className="relative z-10 min-h-screen flex flex-col items-center justify-center px-5 md:px-8 py-[35px] md:py-16">
        {/* Logo */}
        <motion.div 
          className="mb-6"
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span className="text-xl md:text-2xl font-bold text-white">
            BrandContent<span className="bg-gradient-to-r from-violet-400 to-pink-400 bg-clip-text text-transparent">Flow</span>
          </span>
        </motion.div>

        {/* Badge */}
        <motion.span 
          className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 backdrop-blur-sm border border-white/20 rounded-full text-sm font-medium text-white mb-8"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
        >
          <Sparkles className="w-4 h-4 text-violet-300" />
          AI Content Engine
        </motion.span>

        {/* Core Idea Element */}
        <div className="mb-10">
          <CoreIdeaElement />
        </div>

        {/* Headline */}
        <motion.h2 
          className="text-4xl md:text-5xl lg:text-6xl font-black text-center mb-4 max-w-4xl"
          style={{ textWrap: 'balance' }}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
        >
          <span className="text-white">One Blog Post Becomes</span>
          <br />
          <span className="bg-gradient-to-r from-violet-400 via-pink-400 to-orange-400 bg-clip-text text-transparent">
            A Week of Content
          </span>
        </motion.h2>

        {/* Subtitle */}
        <motion.p 
          className="text-white text-base md:text-lg text-center max-w-2xl mb-10 px-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          Upload your content once — AI transforms it into platform-perfect posts automatically. Powered by AI that understands your brand.
        </motion.p>

        {/* CTA Button */}
        <motion.button
          onClick={() => document.getElementById('final-cta')?.scrollIntoView({ behavior: 'smooth' })}
          className="px-8 py-4 bg-gradient-to-r from-violet-500 to-pink-500 text-white font-semibold rounded-xl hover:opacity-90 transition-opacity flex items-center gap-2 shadow-lg shadow-purple-500/30"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.5 }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
          data-testid="hero-cta-btn"
        >
          Get Early Access
          <ArrowRight className="w-5 h-5" />
        </motion.button>
      </div>
    </section>
  );
};

export default ContentEngineFlow;
